import React from "react";

export enum InterjectedRowType {
  PROMO = "promo",
  RECOMMEND_US = "recommend_us",
  FEEDBACK = "feedback",
}

interface InterjectedRowBase {
  id: string;
  after_index: number;
}

export interface PromoRowItem extends InterjectedRowBase {
  type: InterjectedRowType.PROMO;
  icon?: React.ReactNode;
  title: string;
  description: string;
  /// Not every changelog entry has a link - when absent, the row renders without a CTA button.
  action_label?: string;
  action_url?: string;
}

export interface RecommendUsRowItem extends InterjectedRowBase {
  type: InterjectedRowType.RECOMMEND_US;
  variant_id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  action_label: string;
}

export interface FeedbackRowItem extends InterjectedRowBase {
  type: InterjectedRowType.FEEDBACK;
  placeholder: string;
  /// Stable A/B variant id for analytics - independent of the placeholder text itself, so a copy
  /// edit later doesn't fragment historical data for what's conceptually the same variant.
  variant_id: string;
  action_label: string;
}

export type InterjectedRow = PromoRowItem | RecommendUsRowItem | FeedbackRowItem;

/**
 * Everything a table needs to render its interjected rows, passed as one prop so the feature
 * travels together instead of sprawling across BaseTable's prop list.
 */
export interface TableInterjections {
  rows: InterjectedRow[];
  onDismiss: (id: string) => void;
  onFeedbackSubmit?: (row: FeedbackRowItem, value: string) => void;
}

/**
 * Groups interjections by the data row they follow.
 *
 * An after_index past the end of what's loaded is clamped rather than dropped - e.g. a cadence
 * row anchored to ticket #1000 should still be visible (above the last loaded row) rather than
 * staying invisible until the user scrolls 1000 rows deep. Clamped to loadedRowCount - 2 (one
 * before the last loaded row) rather than loadedRowCount - 1, so it renders ABOVE the last loaded
 * row instead of after it - "after_index: X" means the row renders between data row X and X+1,
 * so to land above the final row it has to anchor to the second-to-last one. Only a negative
 * index, or no rows loaded at all (nothing to anchor to yet), is dropped.
 */
export function groupInterjectionsByIndex(rows: InterjectedRow[] | undefined, loadedRowCount: number) {
  const grouped = new Map<number, InterjectedRow[]>();
  if (!rows?.length || loadedRowCount <= 0) return grouped;

  for (const row of rows) {
    if (row.after_index < 0) continue;

    const index = Math.min(row.after_index, Math.max(loadedRowCount - 2, 0));

    const existing = grouped.get(index);
    if (existing) {
      existing.push(row);
      continue;
    }
    grouped.set(index, [row]);
  }

  return grouped;
}
